import type { Option } from "@tento-lona/sheets";

/**
 * The observer lists behind a calendar days strip's harness seam
 * (`CalendarDaysStripInstrumentation.onBind` / `onPaint`). DOM-free, so the
 * package's own harness tests it without an element.
 *
 * A strip holds `null` in place of a list until the first observer attaches,
 * and returns to `null` when the last one leaves: a bind or a paint nobody
 * observes costs one null check and allocates nothing.
 */

/**
 * An observer of one call: it runs before the call with the call's event.
 * A function it returns runs after the call returns or throws, so a harness
 * can bracket the call (time it, or record what it produced).
 */
export type StripObserver<E> = (event: E) => void | (() => void);

/** The observers attached to one strip for one kind of call. */
export class StripObserverList<E> {
  readonly #observers = new Set<StripObserver<E>>();

  /** How many observers are attached. */
  get size(): number {
    return this.#observers.size;
  }

  /**
   * Attach `observer`; returns what detaches it. The same function attached
   * twice runs twice, and each detach removes one of them.
   */
  add(observer: StripObserver<E>): () => void {
    const entry: StripObserver<E> = (event) => observer(event);
    this.#observers.add(entry);
    return () => {
      this.#observers.delete(entry);
    };
  }

  /**
   * Run every observer, in attach order, with `event`. Returns the function
   * that runs what they returned (newest first, so brackets nest), or `null`
   * when none returned one. An observer attached while this runs sees the
   * next call, not this one.
   */
  notify(event: E): Option<() => void> {
    let after: Option<(() => void)[]> = null;
    for (const observer of [...this.#observers]) {
      const settle = observer(event);
      if (typeof settle === "function") (after ??= []).push(settle);
    }
    if (after == null) return null;
    const settles = after;
    settles.reverse();
    return () => {
      for (const settle of settles) settle();
    };
  }
}

/** Where a strip keeps one list: `null` while nothing observes it. */
export interface StripObserverSlot<E> {
  get(): Option<StripObserverList<E>>;
  set(list: Option<StripObserverList<E>>): void;
}

/**
 * Attach `observer` to the list in `slot`, creating the list on the first
 * attach. The returned function detaches it (a second call does nothing)
 * and empties the slot back to `null` when the last observer leaves.
 */
export function attachStripObserver<E>(
  slot: StripObserverSlot<E>,
  observer: StripObserver<E>,
): () => void {
  const list = slot.get() ?? new StripObserverList<E>();
  slot.set(list);
  const detach = list.add(observer);
  let attached = true;
  return () => {
    if (!attached) return;
    attached = false;
    detach();
    if (list.size === 0 && slot.get() === list) slot.set(null);
  };
}
